'use client';

import { useCallback, useRef, useState } from 'react';
import { number, plural } from '@frameio/i18n';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { Button2, Dialog, Text, theme } from '@frameio/vapor';
import { IconInfo } from '@frameio/vapor-icons';
import { DialogFooter } from '../components/DialogFooter';
import { DialogHeader } from '../components/DialogHeader';
import { Divider } from '../components/Divider';
import { useFireflyRemoveBackgroundAction } from '../hooks/useRemoveBackgroundAction';
import styles from './FireflyRemoveBackgroundDialog.module.css';
import TEXT from './generated/TEXT/FireflyRemoveBackgroundDialog';

TEXT.DEFINE({
  /** Name of the remove background action. */
  actionName: 'Remove Background',
  /** Confirmation copy describing how many images will be processed. */
  subtitle: ({ assetCount }: { assetCount: number }): string =>
    plural(assetCount, {
      one: 'Remove the background from the selected image.',
      other: `Remove the background from ${number(
        assetCount
      )} selected images.`,
    }),
  /** Informational note describing the output of the action. */
  outputInfo:
    'Output will be PNGs with transparent backgrounds in a new folder.',
  /** Confirm button text. */
  confirm: 'Remove Background',
  /** Cancel button text. */
  cancel: 'Cancel',
  /** Aria label to close the dialog. */
  dialogCloseLabel: 'Close Dialog',
});

type FireflyRemoveBackgroundDialogProps = {
  assetIds?: string[];
  onClose?: () => void;
};

export const FireflyRemoveBackgroundDialog = ({
  assetIds = [],
  onClose,
}: FireflyRemoveBackgroundDialogProps) => {
  const track = useTrackUnsafe();
  const { removeBackground, rateLimitDialog } =
    useFireflyRemoveBackgroundAction();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasConfirmedRef = useRef(false);
  const closedRef = useRef(false);

  const handleConfirm = useCallback(async () => {
    if (assetIds.length === 0) return;
    hasConfirmedRef.current = true;
    setIsSubmitting(true);
    const started = await removeBackground({ assetIds });
    // in case the dialog is closed while the request is in flight
    if (closedRef.current) return;
    setIsSubmitting(false);
    // Don't close the modal when the action didn't start (e.g. a rate-limit or
    // generic error) so the user can see the feedback and adjust or retry.
    if (started) {
      onClose?.();
    }
  }, [assetIds, removeBackground, onClose]);

  const handleClose = useCallback(() => {
    closedRef.current = true;
    if (!hasConfirmedRef.current) {
      track('generation-dismissed', {
        action_name: 'Remove Background',
        number_of_assets: assetIds.length,
        parameters_selected: 0,
      });
    }
    onClose?.();
  }, [track, assetIds.length, onClose]);

  return (
    <Dialog
      enableClickOutsideToClose={false}
      isShowing={true}
      onRequestClose={handleClose}
      size="small"
    >
      <DialogHeader
        closeLabel={TEXT.dialogCloseLabel}
        onClose={handleClose}
        subtitle={TEXT.subtitle({ assetCount: assetIds.length })}
        title={TEXT.actionName}
      />

      <Divider />

      <div className={styles.infoRow}>
        <IconInfo color={theme.colors.icon.secondary} />
        <Text color="secondary" variant="body">
          {TEXT.outputInfo}
        </Text>
      </div>

      <Divider />

      <DialogFooter>
        <Button2 onPress={handleClose} variant="secondary">
          {TEXT.cancel}
        </Button2>
        <Button2
          isDisabled={assetIds.length === 0 || isSubmitting}
          isLoading={isSubmitting}
          onPress={handleConfirm}
          variant="primary"
        >
          {TEXT.confirm}
        </Button2>
      </DialogFooter>
      {rateLimitDialog}
    </Dialog>
  );
};
